<template>
    <div v-if="loaded">
        <card-header icon="fa-flag-swallowtail" :title="milestone.course_title"/>

        <card-body>
            <card-list>
                <!-- Student Information -->
                <subheader-list-item title="Student Information" icon="fa-user"/>
                <data-row title="Name">{{ milestone.first_name }} {{ milestone.last_name }}</data-row>
                <data-row title="Email">{{ milestone.email }}</data-row>

                <!-- Milestone Information -->
                <subheader-list-item title="Milestone Information" icon="fa-flag-swallowtail"/>
                <data-row title="Milestone">{{ milestone.milestone_number }} of {{ milestone.total_milestones }}</data-row>
                <data-row title="Hour Threshold">{{ getMilestoneRange() }}</data-row>
                <data-row title="Submitted">{{ milestone.submitted_at | moment('MMMM Do, YYYY [at] h:mm A') }}</data-row>

                <!-- AI Instructions (what student was asked to do) -->
                <template v-if="milestone.instructions">
                    <subheader-list-item title="Instructions Given" icon="fa-clipboard-list"/>
                    <container-list-item>
                        <div class="ai-content" v-html="milestone.instructions"></div>
                    </container-list-item>
                </template>

                <!-- Student's Reflection -->
                <subheader-list-item title="Student's Reflection" icon="fa-pen-fancy"/>
                <container-list-item>
                    <p class="content-text">{{ milestone.reflection_text }}</p>
                </container-list-item>

                <!-- Previous Milestones (collapsible history) -->
                <template v-if="previousMilestones.length > 0">
                    <subheader-list-item title="Previous Milestones" icon="fa-history"/>
                    <container-list-item>
                        <b-collapse
                            v-for="pm in previousMilestones"
                            :key="pm.id"
                            :open="false"
                            class="previous-milestone mb-3"
                        >
                            <template #trigger="props">
                                <div class="collapse-header">
                                    <i class="far fa-check-circle has-text-success mr-2"></i>
                                    <strong>Milestone {{ pm.milestone_number }}</strong>
                                    <span class="has-text-grey ml-2">({{ getPrevMilestoneRange(pm) }})</span>
                                    <i class="fas ml-auto" :class="props.open ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                                </div>
                            </template>
                            <div class="collapse-content mt-3">
                                <label class="label is-size-7 has-text-grey">Student's Reflection:</label>
                                <p class="content-text">{{ pm.reflection_text }}</p>
                            </div>
                        </b-collapse>
                    </container-list-item>
                </template>

                <!-- AI Generation Section for Next Milestone -->
                <template v-if="hasNextMilestone">
                    <subheader-list-item title="Next Milestone Generation" icon="fa-robot"/>
                    <container-list-item>
                        <b-field label="Additional Guidance for Next Milestone (Optional)">
                            <b-input
                                type="textarea"
                                v-model="additionalGuidance"
                                placeholder="Provide any additional guidance for the AI to consider when generating the next milestone instructions..."
                                rows="3"
                            />
                        </b-field>

                        <div class="buttons mt-4">
                            <b-button
                                type="is-info"
                                size="is-small"
                                @click="generatePreview"
                                :loading="generating"
                                :disabled="generating"
                            >
                                <i class="fas fa-magic mr-2"></i>
                                {{ nextMilestonePreview ? 'Regenerate Preview' : 'Preview Next Milestone' }}
                            </b-button>
                        </div>

                        <!-- Preview of Next Milestone Instructions -->
                        <template v-if="nextMilestonePreview">
                            <label class="label is-size-7 has-text-grey mt-4">Preview for Milestone {{ milestone.milestone_number + 1 }}:</label>
                            <div class="ai-content preview-content" v-html="nextMilestonePreview"></div>
                        </template>
                    </container-list-item>
                </template>
            </card-list>
        </card-body>

        <card-footer>
            <div class="columns">
                <div class="column">
                    <b-button type="is-warning" size="is-small" @click="openReject">
                        <i class="fas fa-comment-dots mr-2"></i>
                        Request Changes
                    </b-button>
                </div>
                <div class="column has-text-right">
                    <b-button
                        type="is-success"
                        size="is-small"
                        @click="approve"
                        :loading="approving"
                    >
                        <i class="fas fa-check mr-2"></i>
                        Approve
                    </b-button>
                </div>
            </div>
        </card-footer>
    </div>
</template>

<script>
    import CardHeader from "@/TIER/components/CardHeader";
    import CardBody from "@/TIER/components/CardBody";
    import CardFooter from "@/TIER/components/CardFooter";
    import CardList from "@/TIER/components/CardList";
    import SubheaderListItem from "@/TIER/components/SubheaderListItem";
    import ContainerListItem from "@/TIER/components/ContainerListItem";
    import DataRow from "@/TIER/components/DataRow";
    import {client as http} from "@/http_client";
    import {mapActions} from "vuex";

    export default {
        components: {DataRow, ContainerListItem, SubheaderListItem, CardList, CardFooter, CardBody, CardHeader},
        props: ['card', 'props'],
        data() {
            return {
                milestone: null,
                loaded: false,
                approving: false,
                generating: false,
                additionalGuidance: '',
                nextMilestonePreview: null
            };
        },
        computed: {
            previousMilestones() {
                if (!this.milestone || !this.milestone.all_milestones) return [];
                return this.milestone.all_milestones
                    .filter(m => m.milestone_number < this.milestone.milestone_number && m.status === 'approved')
                    .sort((a, b) => b.milestone_number - a.milestone_number);
            },
            hasNextMilestone() {
                if (!this.milestone || !this.milestone.all_milestones) return false;
                return this.milestone.all_milestones.some(m => m.milestone_number > this.milestone.milestone_number);
            }
        },
        methods: {
            ...mapActions(['getMilestonesCount']),
            getMilestoneRange() {
                if (!this.milestone || !this.milestone.all_milestones) return '';
                const prevThreshold = this.milestone.all_milestones
                    .filter(m => m.milestone_number < this.milestone.milestone_number)
                    .reduce((max, m) => Math.max(max, m.hour_threshold), 0);
                return `${prevThreshold}-${this.milestone.hour_threshold} hours`;
            },
            getPrevMilestoneRange(pm) {
                if (!this.milestone || !this.milestone.all_milestones) return '';
                const prevThreshold = this.milestone.all_milestones
                    .filter(m => m.milestone_number < pm.milestone_number)
                    .reduce((max, m) => Math.max(max, m.hour_threshold), 0);
                return `${prevThreshold}-${pm.hour_threshold} hours`;
            },
            load() {
                this.$emit('loading', true);
                http.get(`/milestones/${this.props.milestoneId}`, {force: true}).then(response => {
                    this.milestone = response.data;
                    this.loaded = true;
                    this.$emit('loading', false);
                }).catch(err => {
                    console.error('Error loading milestone:', err);
                    this.$emit('loading', false);
                    this.$buefy.toast.open({
                        message: 'Error loading milestone',
                        type: 'is-danger'
                    });
                });
            },
            generatePreview() {
                this.generating = true;
                http.post(`/milestones/${this.props.milestoneId}/regenerate`, {
                    additional_guidance: this.additionalGuidance
                }).then(response => {
                    this.nextMilestonePreview = response.data.instructions;
                    this.$buefy.toast.open({
                        message: 'Preview generated',
                        type: 'is-success'
                    });
                }).catch(err => {
                    console.error('Error generating preview:', err);
                    this.$buefy.toast.open({
                        message: err.response?.data?.error || 'Error generating preview',
                        type: 'is-danger'
                    });
                }).finally(() => {
                    this.generating = false;
                });
            },
            openReject() {
                this.$openCard('milestone-reject', {milestoneId: this.props.milestoneId}, this.card);
            },
            approve() {
                this.$buefy.dialog.confirm({
                    title: 'Approve Reflection',
                    message: 'Are you sure you want to approve this reflection? The student will be notified and the next milestone will be generated.',
                    confirmText: 'Approve',
                    type: 'is-success',
                    onConfirm: () => {
                        this.approving = true;
                        http.post(`/milestones/${this.props.milestoneId}/approve`, {
                            additional_guidance: this.additionalGuidance,
                            next_milestone_instructions: this.nextMilestonePreview
                        }).then(() => {
                            this.$buefy.toast.open({
                                message: 'Reflection approved',
                                type: 'is-success'
                            });
                            this.getMilestonesCount();
                            this.$closeCard(this.card);
                            this.$reloadCard('milestones');
                        }).catch(err => {
                            console.error('Error approving milestone:', err);
                            this.$buefy.toast.open({
                                message: 'Error approving reflection',
                                type: 'is-danger'
                            });
                        }).finally(() => {
                            this.approving = false;
                        });
                    }
                });
            }
        },
        watch: {
            'props.milestoneId': function() {
                this.load();
            }
        },
        mounted() {
            this.load();
        }
    };
</script>

<style lang="scss" scoped>
    .content-text {
        white-space: pre-wrap;
        line-height: 1.6;
    }

    .ai-content {
        padding: 1rem;
        background: #fafafa;
        border-radius: 4px;

        ::v-deep {
            h4, h5 {
                margin-top: 1rem;
                margin-bottom: 0.5rem;
                color: #363636;
            }

            ul, ol {
                margin-left: 1.5rem;
                margin-bottom: 1rem;
            }

            li {
                margin-bottom: 0.25rem;
            }

            p {
                margin-bottom: 0.75rem;
            }
        }
    }

    .preview-content {
        border: 2px dashed #3273dc;
    }

    .previous-milestone {
        border: 1px solid #eee;
        border-radius: 4px;
        padding: 0.75rem;

        .collapse-header {
            display: flex;
            align-items: center;
            cursor: pointer;
        }

        .collapse-content {
            padding-left: 1.5rem;
            border-top: 1px solid #eee;
            padding-top: 0.75rem;
        }
    }
</style>
